import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';

import { api, ApiError } from '../api/client';
import './Transformation.css';

type TransformKind = 'serken' | 'regular';

interface BucketSummary {
  total: number;
  untransformed: number;
  with_creds: number;
}

interface BucketSummaryResp {
  serken: BucketSummary;
  regular: BucketSummary;
}

interface InventoryDataItem {
  inventory_panel_id: string;
  email: string;
  capacity: number;
  free: number | null;
  has_creds: boolean;
  transformed_at: string | null;
}

interface RunInfo {
  id: string;
  kind: TransformKind;
  status: 'queued' | 'running' | 'completed' | 'failed';
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  panels_pushed_to_it: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  triggered_by: string | null;
  error_message: string | null;
  auto_retry_enabled: boolean;
  auto_retry_attempts_used: number;
  auto_retry_attempts_cap: number;
}

interface CreateRunResp {
  run_id: string;
  panels_enqueued: { inventory_panel_id: string; panel_email: string }[];
  panels_skipped: { inventory_panel_id: string; reason: string }[];
}

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

interface Props {
  onOpenRun: (runId: string) => void;
}

export function Transformation({ onOpenRun }: Props): JSX.Element {
  const [kind, setKind] = useState<TransformKind>('serken');
  const [summary, setSummary] = useState<BucketSummaryResp | null>(null);
  const [items, setItems] = useState<InventoryDataItem[] | null>(null);
  const [recent, setRecent] = useState<RunInfo[]>([]);
  const [filterFree, setFilterFree] = useState<number>(1);
  const [hideTransformed, setHideTransformed] = useState(true);
  const [hideMissingCreds, setHideMissingCreds] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Index (into `filtered`) of the last checkbox the operator clicked.
   *  Anchors shift-click range selection. Reset whenever `filtered` changes
   *  identity (kind / filter / clear) so a range never spans a stale list. */
  const [anchorIndex, setAnchorIndex] = useState<number | null>(null);
  const [polling, setPolling] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const loadAll = useCallback(async (): Promise<void> => {
    try {
      const [s, d, r] = await Promise.all([
        api<BucketSummaryResp>('/api/transformation/bucket-summary'),
        api<{ items: InventoryDataItem[] }>(`/api/transformation/inventory-data?kind=${kind}`),
        api<{ runs: RunInfo[] }>(`/api/transformation/runs?kind=${kind}&limit=10`),
      ]);
      setSummary(s);
      setItems(d.items);
      setRecent(r.runs);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load transformation data');
    }
  }, [kind, showToast]);

  useEffect(() => {
    setItems(null);
    setSelected(new Set());
    setAnchorIndex(null);
    void loadAll();
  }, [kind, loadAll]);

  const filtered = useMemo(() => {
    if (!items) return [];
    return items.filter((it) => {
      if (hideTransformed && it.transformed_at !== null) return false;
      if (hideMissingCreds && !it.has_creds) return false;
      if (it.free !== filterFree) return false;
      return true;
    });
  }, [items, hideTransformed, hideMissingCreds, filterFree]);

  /** Per-bucket counts (0..19 free) after applying hide-transformed +
   *  hide-missing-creds. Lets operators see at a glance which free buckets
   *  have eligible panels before clicking; clicking a cell sets filterFree. */
  const FREE_BUCKETS = 20;
  const freeBuckets = useMemo(() => {
    const buckets = new Array<number>(FREE_BUCKETS).fill(0);
    if (!items) return buckets;
    for (const it of items) {
      if (hideTransformed && it.transformed_at !== null) continue;
      if (hideMissingCreds && !it.has_creds) continue;
      const f = it.free;
      if (typeof f !== 'number') continue;
      if (f < 0 || f >= FREE_BUCKETS) continue;
      buckets[f] += 1;
    }
    return buckets;
  }, [items, hideTransformed, hideMissingCreds]);
  const maxBucketCount = freeBuckets.reduce((m, c) => (c > m ? c : m), 0);

  /** Toggle one row, or - when shift is held and we have an anchor - apply the
   *  clicked row's resulting state to the whole contiguous range between the
   *  anchor and this row (Gmail-style). `index` is into the current `filtered`
   *  list, which is also what the anchor indexes, so the range is well-defined. */
  function handleRowToggle(index: number, shiftKey: boolean): void {
    const item = filtered[index];
    if (!item) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const willSelect = !next.has(item.inventory_panel_id);
      if (shiftKey && anchorIndex !== null && anchorIndex < filtered.length) {
        const [lo, hi] =
          anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
        for (let i = lo; i <= hi; i++) {
          const id = filtered[i].inventory_panel_id;
          if (willSelect) next.add(id);
          else next.delete(id);
        }
      } else if (willSelect) {
        next.add(item.inventory_panel_id);
      } else {
        next.delete(item.inventory_panel_id);
      }
      return next;
    });
    setAnchorIndex(index);
  }

  function selectAllInFilter(): void {
    setSelected(new Set(filtered.map((f) => f.inventory_panel_id)));
  }

  function clearSelection(): void {
    setSelected(new Set());
    setAnchorIndex(null);
  }

  async function startTransformation(): Promise<void> {
    const ids = [...selected];
    if (ids.length === 0) return;
    try {
      const r = await api<CreateRunResp>('/api/transformation/runs', {
        method: 'POST',
        body: JSON.stringify({ kind, inventory_panel_ids: ids }),
      });
      if (r.run_id) {
        const skippedNote =
          r.panels_skipped.length > 0
            ? ` (skipped ${r.panels_skipped.length}: ${[...new Set(r.panels_skipped.map((s) => s.reason))].join(', ')})`
            : '';
        showToast('success', `Run started — ${r.panels_enqueued.length} panels${skippedNote}`);
        onOpenRun(r.run_id);
      } else {
        showToast(
          'error',
          `Nothing to enqueue. Skipped: ${r.panels_skipped.map((s) => `${s.inventory_panel_id}=${s.reason}`).join(', ')}`,
        );
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to start';
      showToast('error', msg);
    }
  }

  async function refreshInventory(): Promise<void> {
    setPolling(true);
    try {
      try {
        await api<{ runId: string; kind: string; panelsQueued: number }>(
          '/api/inventory-runs',
          { method: 'POST', body: JSON.stringify({ kind }) },
        );
      } catch (err) {
        if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
          showToast('info', 'Inventory poll already in progress — waiting for it to finish.');
        } else {
          throw err;
        }
      }
      const start = Date.now();
      let lastFinishedAt = await fetchLatestFinishedAt();
      while (Date.now() - start < 5 * 60_000) {
        await new Promise((r) => setTimeout(r, 4000));
        const fa = await fetchLatestFinishedAt();
        if (fa && fa !== lastFinishedAt) {
          lastFinishedAt = fa;
          await loadAll();
          showToast('success', 'Inventory data refreshed.');
          return;
        }
      }
      showToast('error', 'Inventory poll did not complete within 5 minutes.');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Refresh failed';
      showToast('error', msg);
    } finally {
      setPolling(false);
    }
  }

  async function fetchLatestFinishedAt(): Promise<string | null> {
    try {
      const r = await api<{ id: string; status: string; finished_at: string | null }[]>(
        `/api/inventory-runs?kind=${kind}`,
      );
      const completed = r.find((x) => x.status === 'completed' && x.finished_at);
      return completed?.finished_at ?? null;
    } catch {
      return null;
    }
  }

  const bucketCount =
    summary && (kind === 'serken' ? summary.serken.untransformed : summary.regular.untransformed);

  return (
    <div className="trf-root">
      <div className="trf-scroll">
        <header className="trf-header">
          <h1 className="trf-title">License Panel Transformation</h1>
          <p className="trf-subtitle">
            Transform inventory panels (Serken / Regular) into license-assignment-ready panels.
            Each successful transform: logs in → adds our DWD → adds Cloud Identity → disables
            auto-licensing → removes admin&apos;s license → renames the org. On success, capacity
            bumps 18 → 19 and the panel moves into the <code>panels</code> table where License
            Assignment can pick it up.
          </p>
        </header>

        {toast && <div className={`trf-toast trf-toast--${toast.type}`}>{toast.message}</div>}

        <section className="trf-card">
          <div className="trf-card-header">
            <h2 className="trf-card-title">Select panels to transform</h2>
            <button
              type="button"
              className="trf-btn trf-btn--secondary"
              onClick={() => void refreshInventory()}
              disabled={polling}
              title="Run a fresh inventory poll for the chosen bucket. Results auto-load when complete."
            >
              {polling ? 'Polling…' : 'Refresh data — run new inventory poll'}
            </button>
          </div>
          <div className="trf-card-body">
            <div className="trf-kind-tabs">
              <button
                type="button"
                className={`trf-kind-tab ${kind === 'serken' ? 'trf-kind-tab--active' : ''}`}
                onClick={() => setKind('serken')}
              >
                Serken
                {summary && ` · ${summary.serken.untransformed} untransformed`}
              </button>
              <button
                type="button"
                className={`trf-kind-tab ${kind === 'regular' ? 'trf-kind-tab--active' : ''}`}
                onClick={() => setKind('regular')}
              >
                Regular
                {summary && ` · ${summary.regular.untransformed} untransformed`}
              </button>
            </div>

            <div className="trf-filter-row">
              <label className="trf-filter">
                <input
                  type="checkbox"
                  checked={hideTransformed}
                  onChange={(e) => {
                    setHideTransformed(e.target.checked);
                    clearSelection();
                  }}
                />
                Hide already-transformed
              </label>
              <label className="trf-filter">
                <input
                  type="checkbox"
                  checked={hideMissingCreds}
                  onChange={(e) => {
                    setHideMissingCreds(e.target.checked);
                    clearSelection();
                  }}
                />
                Hide panels missing creds
              </label>
            </div>

            <div className="trf-dist-header">
              Filter by free seats - click a tile to load that bucket
            </div>
            <div className="trf-dist-grid">
              {freeBuckets.map((count, i) => {
                const empty = count === 0;
                const active = i === filterFree;
                const intensity = maxBucketCount === 0 ? 0 : count / maxBucketCount;
                const cls = [
                  'trf-dist-cell',
                  empty ? 'trf-dist-cell--empty' : '',
                  active ? 'trf-dist-cell--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    type="button"
                    key={i}
                    className={cls}
                    style={empty ? undefined : ({ '--trf-dist-fill': intensity } as CSSProperties)}
                    onClick={() => {
                      setFilterFree(i);
                      clearSelection();
                    }}
                    title={`${count} panel${count === 1 ? '' : 's'} with ${i} free`}
                  >
                    <span className="trf-dist-cell-key">{i} free</span>
                    <span className="trf-dist-cell-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="trf-actions-row">
              <button
                type="button"
                className="trf-btn trf-btn--ghost"
                onClick={selectAllInFilter}
                disabled={filtered.length === 0}
              >
                Select all in filter ({filtered.length})
              </button>
              <button
                type="button"
                className="trf-btn trf-btn--ghost"
                onClick={clearSelection}
                disabled={selected.size === 0}
              >
                Clear selection
              </button>
              <div className="trf-actions-spacer" />
              <button
                type="button"
                className="trf-btn trf-btn--primary"
                onClick={() => void startTransformation()}
                disabled={selected.size === 0}
              >
                Start transformation ({selected.size} selected)
              </button>
            </div>

            <div className="trf-shift-hint">
              Tip: check one row, then <kbd>Shift</kbd>+click another to select
              everything in between.
            </div>
          </div>

          <div className="trf-table-wrap">
            {items === null ? (
              <div className="trf-empty">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="trf-empty">
                No panels match the current filter
                {bucketCount !== null && ` — ${bucketCount} untransformed total in ${kind}`}.
              </div>
            ) : (
              <table className="trf-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Email</th>
                    <th className="trf-col-num">Cap</th>
                    <th className="trf-col-num">Free</th>
                    <th className="trf-col-center">Creds</th>
                    <th className="trf-col-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it, index) => (
                    <tr
                      key={it.inventory_panel_id}
                      className={selected.has(it.inventory_panel_id) ? 'trf-row--selected' : ''}
                    >
                      <td className="trf-col-center">
                        <input
                          type="checkbox"
                          checked={selected.has(it.inventory_panel_id)}
                          onClick={(e) => handleRowToggle(index, e.shiftKey)}
                          onChange={() => {}}
                        />
                      </td>
                      <td className="trf-cell-mono">{it.email}</td>
                      <td className="trf-col-num">{it.capacity}</td>
                      <td className="trf-col-num">{it.free ?? '—'}</td>
                      <td className="trf-col-center">{it.has_creds ? '✓' : '✗'}</td>
                      <td className="trf-col-center">
                        {it.transformed_at ? (
                          <span className="trf-status trf-status--ok">transformed</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="trf-card">
          <div className="trf-card-header">
            <h2 className="trf-card-title">Recent runs ({kind})</h2>
          </div>
          <div className="trf-table-wrap">
            <table className="trf-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th className="trf-col-center">Status</th>
                  <th className="trf-col-num">OK</th>
                  <th className="trf-col-num">Errored</th>
                  <th className="trf-col-num">To IT</th>
                  <th className="trf-col-num">Total</th>
                  <th>Triggered by</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="trf-empty">
                      No runs yet for {kind}.
                    </td>
                  </tr>
                ) : (
                  recent.map((r) => (
                    <tr
                      key={r.id}
                      className="trf-row--clickable"
                      onClick={() => onOpenRun(r.id)}
                      title="Open run drilldown"
                    >
                      <td className="trf-cell-mono">
                        {new Date(r.created_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="trf-col-center">
                        <span className={`trf-status trf-status--${r.status}`}>{r.status}</span>
                      </td>
                      <td className="trf-col-num">{r.panels_done}</td>
                      <td className="trf-col-num">{r.panels_errored}</td>
                      <td className="trf-col-num">{r.panels_pushed_to_it}</td>
                      <td className="trf-col-num">{r.panels_total}</td>
                      <td className="trf-cell-faint">{r.triggered_by ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
